// @ts-nocheck

import * as Apollo from "@apollo/client";

import gql from "graphql-tag";
import * as APITypes from "../../apiTypes";
import {
  AddressFragmentDoc,
  BillingAddressFragmentDoc,
  BundleAdditionalFragmentDoc,
  BundleChecklistFragmentDoc,
  BundleQuestionFragmentDoc,
  OrderAttributeFragmentDoc,
  OrderDiscountFragmentDoc,
  OrderHouseAreaFragmentDoc,
  OrderPeriodicityFragmentDoc,
  OrderProcessingFeeFragmentDoc,
  OrderServiceFragmentDoc,
  PaymentFragmentDoc,
  SubscriptionProcessingFeeFragmentDoc,
  SwapFieldsFragmentDoc,
  V2BundleFragmentDoc,
} from "./fragments.generated";

const defaultOptions = {} as const;
export type CheckoutFragment = {
  couponCode?: string | null;
  currency: string;
  customerNote?: string | null;
  fingerprint?: string | null;
  id: string;
  locationId: string;
  paymentMethodId?: string | null;
  priceAfterDiscounts?: number | null;
  propertyId?: string | null;
  startDate?: string | null;
  timeConsumption: string;
  totalPrice: number;
  useVirtualWallet?: boolean | null;
  address?: {
    city: string;
    doorCode?: string | null;
    floorArea?: number | null;
    notes?: string | null;
    postalOrZipCode: string;
    streetAddress1: string;
    streetAddress2?: string | null;
    type?: APITypes.ApartmentType | null;
  } | null;
  attributes: Array<{
    currency: string;
    price: number;
    quantity?: number | null;
    timeConsumption?: string | null;
    attribute: {
      description?: string | null;
      id: string;
      isRequired?: boolean | null;
      name: string;
      position: number;
      type: APITypes.AttributeType;
      options: Array<{
        description?: string | null;
        id: string;
        isFeatured: boolean;
        name: string;
        position: number;
        price: number;
        timeConsumption: string;
      }>;
    };
    values: Array<{
      description?: string | null;
      id: string;
      isFeatured: boolean;
      name: string;
      position: number;
      price: number;
      timeConsumption: string;
    } | null>;
  } | null>;
  billingAddress?: {
    city?: string | null;
    country?: string | null;
    email?: string | null;
    line1?: string | null;
    line2?: string | null;
    name: string;
    postal_code?: string | null;
    state?: string | null;
    taxIdentification?: string | null;
  } | null;
  bundle: {
    categoryType?: APITypes.CategoryType | null;
    id: string;
    name: string;
    slug: string;
    startingPrice?: number | null;
    houseArea?: { id: string; enabled: boolean } | null;
  };
  discounts: Array<{ fixedValue: number; id: string; type: APITypes.OrderDiscountType } | null>;
  orderHouseArea?: {
    houseAreaAttributeId: string;
    houseAreaStepId: string;
    price: any;
    timeConsumption: string;
    discount?: Array<{
      fixedValue: number;
      id: string;
      type: APITypes.OrderDiscountType;
    } | null> | null;
  } | null;
  periodicity?: {
    cycleDuration: string;
    description: string;
    discount: number;
    id: string;
    title: string;
  } | null;
  services: Array<{
    currency: string;
    price: number;
    timeConsumption: string;
    service: {
      additionalDescription: string;
      description: string;
      icon: string;
      id: string;
      name: string;
      variants: Array<{
        description: string;
        id: string;
        price: number;
        isFeatured: boolean;
        isFree: boolean;
        timeConsumption: string;
        title: string;
      }>;
    };
    variants: Array<{
      currency: string;
      description: string;
      isFree?: boolean | null;
      price: number;
      quantity: number;
      sumPrice: any;
      sumTimeConsumption: string;
      timeConsumption: string;
      title: string;
      variantId: string;
    }>;
  } | null>;
};

export type V2CheckoutFragment = {
  couponCode?: string | null;
  currency: string;
  customerNote?: string | null;
  fingerprint?: string | null;
  id: string;
  locationId: string;
  paymentMethodId?: string | null;
  priceAfterDiscounts: number;
  propertyId?: string | null;
  startDate?: string | null;
  timeConsumption: string;
  totalPrice: number;
  useVirtualWallet?: boolean | null;
  address?: {
    city: string;
    doorCode?: string | null;
    floorArea?: number | null;
    notes?: string | null;
    postalOrZipCode: string;
    streetAddress1: string;
    streetAddress2?: string | null;
    type?: APITypes.ApartmentType | null;
  } | null;
  attributes: Array<{
    currency: string;
    price: number;
    quantity?: number | null;
    timeConsumption?: string | null;
    attribute: {
      description?: string | null;
      id: string;
      isRequired: boolean;
      name: string;
      position: number;
      type: APITypes.AttributeType;
      answers: Array<{
        description: string;
        id: string;
        isFeatured: boolean;
        name: string;
        position: number;
        price: number;
        timeConsumption: string;
      }>;
    };
    values: Array<{
      description: string;
      id: string;
      isFeatured: boolean;
      name: string;
      position: number;
      price: number;
      timeConsumption: string;
    }>;
  }>;
  billingAddress?: {
    city?: string | null;
    country?: string | null;
    email?: string | null;
    line1?: string | null;
    line2?: string | null;
    name: string;
    postal_code?: string | null;
    state?: string | null;
    taxIdentification?: string | null;
  } | null;
  bundle: {
    categoryType: APITypes.CategoryType;
    id: string;
    name: string;
    slug: string;
    startingPrice: number;
    minimumOrderValue?: number | null;
    minimumOrderValueEnabled?: boolean | null;
    houseArea?: { id: string; enabled: boolean } | null;
  };
  discounts?: Array<{ fixedValue: number; id: string; type: APITypes.Order_Discount_Type }> | null;
  orderHouseArea?: {
    houseAreaAttributeId: string;
    houseAreaStepId: string;
    price: number;
    timeConsumption: string;
    discount?: Array<{ fixedValue: number; id: string; type: APITypes.Order_Discount_Type }> | null;
  } | null;
  periodicity?: {
    cycleDuration: string;
    description: string;
    discount?: string | null;
    id: string;
    title: string;
  } | null;
  services: Array<{
    currency: string;
    price: number;
    timeConsumption: string;
    service: {
      additionalDescription: string;
      description: string;
      icon: string;
      id: string;
      name: string;
      variants: Array<{
        description: string;
        id: string;
        price: number;
        isFeatured: boolean;
        isFree: boolean;
        timeConsumption: string;
        title: string;
      }>;
    };
    variants: Array<{
      currency: string;
      description: string;
      isFree?: string | null;
      price: number;
      quantity: number;
      sumPrice: number;
      sumTimeConsumption: string;
      timeConsumption: string;
      title: string;
      variantId: string;
    }>;
  }>;
};

export type V2GetBundleByIdQueryVariables = APITypes.Exact<{
  id: APITypes.Scalars["String"]["input"];
  bundleId?: APITypes.InputMaybe<APITypes.Scalars["String"]["input"]>;
}>;

export type V2GetBundleByIdQuery = {
  v2GetBundleById: {
    additionalDescription: string;
    additionalTitle: string;
    isAdditionalTitleAndDescriptionEnabled: boolean;
    categoryType: APITypes.CategoryType;
    minimumOrderValue?: number | null;
    minimumOrderValueEnabled?: boolean | null;
    checkList: Array<{ description: string; iconUrl: string; position: number; title: string }>;
    categories: Array<{
      id: string;
      description: string;
      title: string;
      questions: Array<{
        description?: string | null;
        additionalDescription: string;
        wysiwygDescription: string;
        iconUrl: string;
        id: string;
        isRequired: boolean;
        isFeatured: boolean;
        name: string;
        position: number;
        type: APITypes.AttributeType;
        numericType?: APITypes.NumericType | null;
        answers: Array<{
          description: string;
          id: string;
          isFeatured: boolean;
          name: string;
          position: number;
          price: number;
          timeConsumption: string;
        }>;
      }>;
    }>;
    questions: Array<{
      description?: string | null;
      additionalDescription: string;
      wysiwygDescription: string;
      iconUrl: string;
      id: string;
      isRequired: boolean;
      isFeatured: boolean;
      name: string;
      position: number;
      type: APITypes.AttributeType;
      numericType?: APITypes.NumericType | null;
      answers: Array<{
        description: string;
        id: string;
        isFeatured: boolean;
        name: string;
        position: number;
        price: number;
        timeConsumption: string;
      }>;
    }>;
    services: Array<{
      additionalDescription: string;
      description: string;
      icon: string;
      id: string;
      name: string;
      variants: Array<{
        description: string;
        id: string;
        price: number;
        isFeatured: boolean;
        isFree: boolean;
        timeConsumption: string;
        title: string;
      }>;
    }>;
  };
};

export type V2GetBundleBySlugQueryVariables = APITypes.Exact<{
  localizationId: APITypes.Scalars["String"]["input"];
  slug: APITypes.Scalars["String"]["input"];
  bundleId?: APITypes.InputMaybe<APITypes.Scalars["String"]["input"]>;
}>;

export type V2GetBundleBySlugQuery = {
  v2GetBundleBySlug: {
    categoryType: APITypes.CategoryType;
    description: string;
    id: string;
    name: string;
    startingPrice: number;
    position: number;
    isAdditionalTitleAndDescriptionEnabled: boolean;
    additionalTitle: string;
    additionalDescription: string;
    slug: string;
    checkList: Array<{ description: string; iconUrl: string; position: number; title: string }>;
    houseArea?: { id: string; enabled: boolean; steps: Array<{ from: number; to: number }> } | null;
    categories: Array<{
      id: string;
      title: string;
      description: string;
      position: number;
      questions: Array<{
        description?: string | null;
        additionalDescription: string;
        wysiwygDescription: string;
        iconUrl: string;
        id: string;
        isRequired: boolean;
        isFeatured: boolean;
        name: string;
        position: number;
        type: APITypes.AttributeType;
        numericType?: APITypes.NumericType | null;
        answers: Array<{
          description: string;
          id: string;
          isFeatured: boolean;
          name: string;
          position: number;
          price: number;
          timeConsumption: string;
        }>;
      }>;
    }>;
    questions: Array<{
      description?: string | null;
      additionalDescription: string;
      wysiwygDescription: string;
      iconUrl: string;
      id: string;
      isRequired: boolean;
      isFeatured: boolean;
      name: string;
      position: number;
      type: APITypes.AttributeType;
      numericType?: APITypes.NumericType | null;
      answers: Array<{
        description: string;
        id: string;
        isFeatured: boolean;
        name: string;
        position: number;
        price: number;
        timeConsumption: string;
      }>;
    }>;
    services: Array<{
      additionalDescription: string;
      description: string;
      icon: string;
      id: string;
      name: string;
      variants: Array<{
        description: string;
        id: string;
        price: number;
        isFeatured: boolean;
        isFree: boolean;
        timeConsumption: string;
        title: string;
      }>;
    }>;
    subscriptionOptions: Array<{
      description: string;
      discount: number;
      duration: string;
      title: string;
      isFeatured: boolean;
      id: string;
    }>;
  };
};

export type EstimateProcessingFeeQueryVariables = APITypes.Exact<{
  reservationId: APITypes.Scalars["String"]["input"];
}>;

export type EstimateProcessingFeeQuery = {
  estimateProcessingFee?: {
    amount: number;
    currency: string;
    type: APITypes.ProcessingFeeType;
  } | null;
};

export type EstimateSubscriptionProcessingFeeQueryVariables = APITypes.Exact<{
  subscriptionId: APITypes.Scalars["String"]["input"];
}>;

export type EstimateSubscriptionProcessingFeeQuery = {
  estimateSubscriptionProcessingFee?: {
    amount: number;
    currency: string;
    promoCode: string;
    promoCodeAlreadyUsed: boolean;
    type: APITypes.ProcessingFeeType;
  } | null;
};

export type EstimateReservationRescheduleQueryVariables = APITypes.Exact<{
  reservationId: APITypes.Scalars["String"]["input"];
  data: APITypes.EstimateReservationRescheduleInput;
}>;

export type EstimateReservationRescheduleQuery = {
  estimateReservationReschedule?: {
    amount: number;
    currency: string;
    type: APITypes.ProcessingFeeType;
  } | null;
};

export type EstimateReservationAdditionalServiceEditQueryVariables = APITypes.Exact<{
  reservationId: APITypes.Scalars["String"]["input"];
  data: APITypes.EstimateReservationAdditionalServiceEditInput;
  bundleId?: APITypes.InputMaybe<APITypes.Scalars["String"]["input"]>;
}>;

export type EstimateReservationAdditionalServiceEditQuery = {
  estimateReservationAdditionalServiceEdit?: {
    newPrice: number;
    newTimeConsumption: string;
    oldPrice: number;
    oldTimeConsumption: string;
    additional: Array<{
      currency: string;
      price: number;
      timeConsumption: string;
      service: {
        additionalDescription: string;
        description: string;
        icon: string;
        id: string;
        name: string;
        variants: Array<{
          description: string;
          id: string;
          price: number;
          isFeatured: boolean;
          isFree: boolean;
          timeConsumption: string;
          title: string;
        }>;
      };
      variants: Array<{
        currency: string;
        description: string;
        isFree?: boolean | null;
        price: number;
        quantity: number;
        sumPrice: any;
        sumTimeConsumption: string;
        timeConsumption: string;
        title: string;
        variantId: string;
      }>;
    }>;
  } | null;
};

export type EstimateReservationAttributeEditQueryVariables = APITypes.Exact<{
  reservationId: APITypes.Scalars["String"]["input"];
  data: APITypes.EstimateReservationAttributeEditInput;
}>;

export type EstimateReservationAttributeEditQuery = {
  estimateReservationAttributeEdit?: {
    newPrice: number;
    newTimeConsumption: string;
    oldPrice: number;
    oldTimeConsumption: string;
    questions: Array<{
      currency: string;
      price: number;
      quantity?: number | null;
      timeConsumption?: string | null;
      attribute: {
        description?: string | null;
        id: string;
        isRequired?: boolean | null;
        name: string;
        position: number;
        type: APITypes.AttributeType;
        options: Array<{
          description?: string | null;
          id: string;
          isFeatured: boolean;
          name: string;
          position: number;
          price: number;
          timeConsumption: string;
        }>;
      };
      values: Array<{
        description?: string | null;
        id: string;
        isFeatured: boolean;
        name: string;
        position: number;
        price: number;
        timeConsumption: string;
      } | null>;
    }>;
  } | null;
};

export type GetPaymentQueryVariables = APITypes.Exact<{
  paymentId: APITypes.Scalars["String"]["input"];
}>;

export type GetPaymentQuery = {
  getPayment: {
    amount: number;
    appliedDiscount: number;
    currency: string;
    id: string;
    paymentMethodId?: string | null;
    paymentUpdateDate?: string | null;
    status: APITypes.PaymentStatus;
    stripePaymentNextStep?:
      | {
          clientSecret: string;
          paymentIntentId: string;
          redirectUrl: string;
          type: APITypes.PaymentActionType;
        }
      | {
          clientSecret: string;
          paymentIntentId: string;
          stripeSdkData: string;
          type: APITypes.PaymentActionType;
        }
      | null;
  };
};

export type ChangePaymentMethodMutationVariables = APITypes.Exact<{
  paymentId: APITypes.Scalars["String"]["input"];
  paymentMethodId: APITypes.Scalars["String"]["input"];
  reservationId: APITypes.Scalars["String"]["input"];
}>;

export type ChangePaymentMethodMutation = {
  changePaymentMethod: {
    amount: number;
    appliedDiscount: number;
    currency: string;
    id: string;
    paymentMethodId?: string | null;
    paymentUpdateDate?: string | null;
    status: APITypes.PaymentStatus;
    stripePaymentNextStep?:
      | {
          clientSecret: string;
          paymentIntentId: string;
          redirectUrl: string;
          type: APITypes.PaymentActionType;
        }
      | {
          clientSecret: string;
          paymentIntentId: string;
          stripeSdkData: string;
          type: APITypes.PaymentActionType;
        }
      | null;
  };
};

export type DeclareRescheduleReservationMutationVariables = APITypes.Exact<{
  reservationId: APITypes.Scalars["String"]["input"];
  data: APITypes.EstimateReservationRescheduleInput;
}>;

export type DeclareRescheduleReservationMutation = {
  declareRescheduleReservation?:
    | {
        id: string;
        fromReservation: { id: string };
        toReservation?: { id: string } | null;
        payment?: {
          id: string;
          status: APITypes.PaymentStatus;
          stripePaymentNextStep?:
            | {
                clientSecret: string;
                paymentIntentId: string;
                redirectUrl: string;
                type: APITypes.PaymentActionType;
              }
            | {
                clientSecret: string;
                paymentIntentId: string;
                stripeSdkData: string;
                type: APITypes.PaymentActionType;
              }
            | null;
        } | null;
      }
    | {
        id: string;
        fromReservation: { id: string };
        toReservation?: { id: string } | null;
        payment: {
          id: string;
          status: APITypes.PaymentStatus;
          stripePaymentNextStep?:
            | {
                clientSecret: string;
                paymentIntentId: string;
                redirectUrl: string;
                type: APITypes.PaymentActionType;
              }
            | {
                clientSecret: string;
                paymentIntentId: string;
                stripeSdkData: string;
                type: APITypes.PaymentActionType;
              }
            | null;
        };
      }
    | null;
};

export type CompleteRescheduleMutationVariables = APITypes.Exact<{
  swapId: APITypes.Scalars["String"]["input"];
}>;

export type CompleteRescheduleMutation = {
  completeReschedule: { id: string; toReservation: { id: string } };
};

export type DeclareReservationAttributeEditMutationVariables = APITypes.Exact<{
  reservationId: APITypes.Scalars["String"]["input"];
  data: APITypes.EstimateReservationAttributeEditInput;
}>;

export type DeclareReservationAttributeEditMutation = {
  declareReservationAttributeEdit?:
    | {
        id: string;
        fromReservation: { id: string };
        toReservation?: { id: string } | null;
        payment?: {
          id: string;
          status: APITypes.PaymentStatus;
          stripePaymentNextStep?:
            | {
                clientSecret: string;
                paymentIntentId: string;
                redirectUrl: string;
                type: APITypes.PaymentActionType;
              }
            | {
                clientSecret: string;
                paymentIntentId: string;
                stripeSdkData: string;
                type: APITypes.PaymentActionType;
              }
            | null;
        } | null;
      }
    | {
        id: string;
        fromReservation: { id: string };
        toReservation?: { id: string } | null;
        payment: {
          id: string;
          status: APITypes.PaymentStatus;
          stripePaymentNextStep?:
            | {
                clientSecret: string;
                paymentIntentId: string;
                redirectUrl: string;
                type: APITypes.PaymentActionType;
              }
            | {
                clientSecret: string;
                paymentIntentId: string;
                stripeSdkData: string;
                type: APITypes.PaymentActionType;
              }
            | null;
        };
      }
    | null;
};

export type CompleteAttributeEditMutationVariables = APITypes.Exact<{
  swapId: APITypes.Scalars["String"]["input"];
}>;

export type CompleteAttributeEditMutation = {
  completeAttributeEdit: { id: string; toReservation: { id: string } };
};

export type DeclareReservationAdditionalEditMutationVariables = APITypes.Exact<{
  reservationId: APITypes.Scalars["String"]["input"];
  data: APITypes.EstimateReservationAdditionalServiceEditInput;
}>;

export type DeclareReservationAdditionalEditMutation = {
  declareReservationAdditionalEdit?:
    | {
        id: string;
        fromReservation: { id: string };
        toReservation?: { id: string } | null;
        payment?: {
          id: string;
          status: APITypes.PaymentStatus;
          stripePaymentNextStep?:
            | {
                clientSecret: string;
                paymentIntentId: string;
                redirectUrl: string;
                type: APITypes.PaymentActionType;
              }
            | {
                clientSecret: string;
                paymentIntentId: string;
                stripeSdkData: string;
                type: APITypes.PaymentActionType;
              }
            | null;
        } | null;
      }
    | {
        id: string;
        fromReservation: { id: string };
        toReservation?: { id: string } | null;
        payment: {
          id: string;
          status: APITypes.PaymentStatus;
          stripePaymentNextStep?:
            | {
                clientSecret: string;
                paymentIntentId: string;
                redirectUrl: string;
                type: APITypes.PaymentActionType;
              }
            | {
                clientSecret: string;
                paymentIntentId: string;
                stripeSdkData: string;
                type: APITypes.PaymentActionType;
              }
            | null;
        };
      }
    | null;
};

export type CompleteAdditionalEditMutationVariables = APITypes.Exact<{
  swapId: APITypes.Scalars["String"]["input"];
}>;

export type CompleteAdditionalEditMutation = {
  completeAdditionalEdit: { id: string; toReservation: { id: string } };
};

export type UpdateReservationCustomerNoteMutationVariables = APITypes.Exact<{
  reservationId: APITypes.Scalars["String"]["input"];
  note: APITypes.Scalars["String"]["input"];
}>;

export type UpdateReservationCustomerNoteMutation = {
  updateReservationCustomerNote: { id: string };
};

export type CancelReservationMutationVariables = APITypes.Exact<{
  reservationId: APITypes.Scalars["String"]["input"];
  reason: APITypes.Scalars["String"]["input"];
}>;

export type CancelReservationMutation = { cancelReservation: { id: string } };

export type V2CancelSubscriptionMutationVariables = APITypes.Exact<{
  subscriptionId: APITypes.Scalars["String"]["input"];
  reason: APITypes.Scalars["String"]["input"];
}>;

export type V2CancelSubscriptionMutation = { v2CancelSubscription: boolean };

export type V2DraftCheckoutMutationVariables = APITypes.Exact<{
  data: APITypes.V2DraftCheckoutInput;
}>;

export type V2DraftCheckoutMutation = {
  v2DraftCheckout: {
    couponCode?: string | null;
    currency: string;
    customerNote?: string | null;
    fingerprint?: string | null;
    id: string;
    locationId: string;
    paymentMethodId?: string | null;
    priceAfterDiscounts: number;
    propertyId?: string | null;
    startDate?: string | null;
    timeConsumption: string;
    totalPrice: number;
    useVirtualWallet?: boolean | null;
    address?: {
      city: string;
      doorCode?: string | null;
      floorArea?: number | null;
      notes?: string | null;
      postalOrZipCode: string;
      streetAddress1: string;
      streetAddress2?: string | null;
      type?: APITypes.ApartmentType | null;
    } | null;
    attributes: Array<{
      currency: string;
      price: number;
      quantity?: number | null;
      timeConsumption?: string | null;
      attribute: {
        description?: string | null;
        id: string;
        isRequired: boolean;
        name: string;
        position: number;
        type: APITypes.AttributeType;
        answers: Array<{
          description: string;
          id: string;
          isFeatured: boolean;
          name: string;
          position: number;
          price: number;
          timeConsumption: string;
        }>;
      };
      values: Array<{
        description: string;
        id: string;
        isFeatured: boolean;
        name: string;
        position: number;
        price: number;
        timeConsumption: string;
      }>;
    }>;
    billingAddress?: {
      city?: string | null;
      country?: string | null;
      email?: string | null;
      line1?: string | null;
      line2?: string | null;
      name: string;
      postal_code?: string | null;
      state?: string | null;
      taxIdentification?: string | null;
    } | null;
    bundle: {
      categoryType: APITypes.CategoryType;
      id: string;
      name: string;
      slug: string;
      startingPrice: number;
      minimumOrderValue?: number | null;
      minimumOrderValueEnabled?: boolean | null;
      houseArea?: { id: string; enabled: boolean } | null;
    };
    discounts?: Array<{
      fixedValue: number;
      id: string;
      type: APITypes.Order_Discount_Type;
    }> | null;
    orderHouseArea?: {
      houseAreaAttributeId: string;
      houseAreaStepId: string;
      price: number;
      timeConsumption: string;
      discount?: Array<{
        fixedValue: number;
        id: string;
        type: APITypes.Order_Discount_Type;
      }> | null;
    } | null;
    periodicity?: {
      cycleDuration: string;
      description: string;
      discount?: string | null;
      id: string;
      title: string;
    } | null;
    services: Array<{
      currency: string;
      price: number;
      timeConsumption: string;
      service: {
        additionalDescription: string;
        description: string;
        icon: string;
        id: string;
        name: string;
        variants: Array<{
          description: string;
          id: string;
          price: number;
          isFeatured: boolean;
          isFree: boolean;
          timeConsumption: string;
          title: string;
        }>;
      };
      variants: Array<{
        currency: string;
        description: string;
        isFree?: string | null;
        price: number;
        quantity: number;
        sumPrice: number;
        sumTimeConsumption: string;
        timeConsumption: string;
        title: string;
        variantId: string;
      }>;
    }>;
  };
};

export type ConfirmCheckoutMutationVariables = APITypes.Exact<{
  checkoutId: APITypes.Scalars["String"]["input"];
}>;

export type ConfirmCheckoutMutation = {
  confirmCheckout: { id: string; type: APITypes.CheckoutConfirmationType };
};

export type V2GetCheckoutQueryVariables = APITypes.Exact<{ [key: string]: never }>;

export type V2GetCheckoutQuery = {
  v2GetCheckout: {
    couponCode?: string | null;
    currency: string;
    customerNote?: string | null;
    fingerprint?: string | null;
    id: string;
    locationId: string;
    paymentMethodId?: string | null;
    priceAfterDiscounts: number;
    propertyId?: string | null;
    startDate?: string | null;
    timeConsumption: string;
    totalPrice: number;
    useVirtualWallet?: boolean | null;
    address?: {
      city: string;
      doorCode?: string | null;
      floorArea?: number | null;
      notes?: string | null;
      postalOrZipCode: string;
      streetAddress1: string;
      streetAddress2?: string | null;
      type?: APITypes.ApartmentType | null;
    } | null;
    attributes: Array<{
      currency: string;
      price: number;
      quantity?: number | null;
      timeConsumption?: string | null;
      attribute: {
        description?: string | null;
        id: string;
        isRequired: boolean;
        name: string;
        position: number;
        type: APITypes.AttributeType;
        answers: Array<{
          description: string;
          id: string;
          isFeatured: boolean;
          name: string;
          position: number;
          price: number;
          timeConsumption: string;
        }>;
      };
      values: Array<{
        description: string;
        id: string;
        isFeatured: boolean;
        name: string;
        position: number;
        price: number;
        timeConsumption: string;
      }>;
    }>;
    billingAddress?: {
      city?: string | null;
      country?: string | null;
      email?: string | null;
      line1?: string | null;
      line2?: string | null;
      name: string;
      postal_code?: string | null;
      state?: string | null;
      taxIdentification?: string | null;
    } | null;
    bundle: {
      categoryType: APITypes.CategoryType;
      id: string;
      name: string;
      slug: string;
      startingPrice: number;
      minimumOrderValue?: number | null;
      minimumOrderValueEnabled?: boolean | null;
      houseArea?: { id: string; enabled: boolean } | null;
    };
    discounts?: Array<{
      fixedValue: number;
      id: string;
      type: APITypes.Order_Discount_Type;
    }> | null;
    orderHouseArea?: {
      houseAreaAttributeId: string;
      houseAreaStepId: string;
      price: number;
      timeConsumption: string;
      discount?: Array<{
        fixedValue: number;
        id: string;
        type: APITypes.Order_Discount_Type;
      }> | null;
    } | null;
    periodicity?: {
      cycleDuration: string;
      description: string;
      discount?: string | null;
      id: string;
      title: string;
    } | null;
    services: Array<{
      currency: string;
      price: number;
      timeConsumption: string;
      service: {
        additionalDescription: string;
        description: string;
        icon: string;
        id: string;
        name: string;
        variants: Array<{
          description: string;
          id: string;
          price: number;
          isFeatured: boolean;
          isFree: boolean;
          timeConsumption: string;
          title: string;
        }>;
      };
      variants: Array<{
        currency: string;
        description: string;
        isFree?: string | null;
        price: number;
        quantity: number;
        sumPrice: number;
        sumTimeConsumption: string;
        timeConsumption: string;
        title: string;
        variantId: string;
      }>;
    }>;
  };
};

export const CheckoutFragmentDoc = gql`
    fragment Checkout on Checkout {
  address {
    ...Address
  }
  attributes {
    ...OrderAttribute
  }
  billingAddress {
    ...BillingAddress
  }
  bundle {
    categoryType
    houseArea {
      id
      enabled
    }
    id
    name
    slug
    startingPrice
  }
  couponCode
  currency
  customerNote
  discounts {
    ...OrderDiscount
  }
  fingerprint
  orderHouseArea {
    ...OrderHouseArea
  }
  id
  locationId
  paymentMethodId
  periodicity {
    ...OrderPeriodicity
  }
  priceAfterDiscounts
  propertyId
  services {
    ...OrderService
  }
  startDate
  timeConsumption
  totalPrice
  useVirtualWallet
}
    ${AddressFragmentDoc}
${OrderAttributeFragmentDoc}
${BillingAddressFragmentDoc}
${OrderDiscountFragmentDoc}
${OrderHouseAreaFragmentDoc}
${OrderPeriodicityFragmentDoc}
${OrderServiceFragmentDoc}`;
export const V2CheckoutFragmentDoc = gql`
    fragment V2Checkout on v2Checkout {
  address {
    city
    doorCode
    floorArea
    notes
    postalOrZipCode
    streetAddress1
    streetAddress2
    type
  }
  attributes {
    attribute {
      description
      id
      isRequired
      name
      answers {
        description
        id
        isFeatured
        name
        position
        price
        timeConsumption
      }
      position
      type
    }
    currency
    price
    quantity
    timeConsumption
    values {
      description
      id
      isFeatured
      name
      position
      price
      timeConsumption
    }
  }
  billingAddress {
    city
    country
    email
    line1
    line2
    name
    postal_code
    state
    taxIdentification
  }
  bundle {
    houseArea {
      id
      enabled
    }
    categoryType
    id
    name
    slug
    startingPrice
    minimumOrderValue
    minimumOrderValueEnabled
  }
  couponCode
  currency
  customerNote
  discounts {
    fixedValue
    id
    type
  }
  fingerprint
  orderHouseArea {
    discount {
      fixedValue
      id
      type
    }
    houseAreaAttributeId
    houseAreaStepId
    price
    timeConsumption
  }
  id
  locationId
  paymentMethodId
  periodicity {
    cycleDuration
    description
    discount
    id
    title
  }
  priceAfterDiscounts
  propertyId
  services {
    currency
    price
    service {
      additionalDescription
      description
      icon
      id
      name
      variants {
        description
        id
        price
        isFeatured
        isFree
        timeConsumption
        title
      }
    }
    timeConsumption
    variants {
      currency
      description
      isFree
      price
      quantity
      sumPrice
      sumTimeConsumption
      timeConsumption
      title
      variantId
    }
  }
  startDate
  timeConsumption
  totalPrice
  useVirtualWallet
}
    `;
export const V2GetBundleByIdDocument = gql`
    query V2GetBundleById($id: String!, $bundleId: String) {
  v2GetBundleById(id: $id) {
    additionalDescription
    additionalTitle
    isAdditionalTitleAndDescriptionEnabled
    categoryType
    minimumOrderValue
    minimumOrderValueEnabled
    checkList {
      ...BundleChecklist
    }
    categories {
      id
      description
      title
      questions {
        ...BundleQuestion
      }
    }
    questions {
      ...BundleQuestion
    }
    services {
      ...BundleAdditional
    }
  }
}
    ${BundleChecklistFragmentDoc}
${BundleQuestionFragmentDoc}
${BundleAdditionalFragmentDoc}`;

/**
 * __useV2GetBundleByIdQuery__
 *
 * To run a query within a React component, call `useV2GetBundleByIdQuery` and pass it any options that fit your needs.
 * When your component renders, `useV2GetBundleByIdQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useV2GetBundleByIdQuery({
 *   variables: {
 *      id: // value for 'id'
 *      bundleId: // value for 'bundleId'
 *   },
 * });
 */
export function useV2GetBundleByIdQuery(
  baseOptions: Apollo.QueryHookOptions<V2GetBundleByIdQuery, V2GetBundleByIdQueryVariables> &
    ({ variables: V2GetBundleByIdQueryVariables; skip?: boolean } | { skip: boolean }),
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery<V2GetBundleByIdQuery, V2GetBundleByIdQueryVariables>(
    V2GetBundleByIdDocument,
    options,
  );
}
export function useV2GetBundleByIdLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<V2GetBundleByIdQuery, V2GetBundleByIdQueryVariables>,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<V2GetBundleByIdQuery, V2GetBundleByIdQueryVariables>(
    V2GetBundleByIdDocument,
    options,
  );
}
export function useV2GetBundleByIdSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | Apollo.SuspenseQueryHookOptions<V2GetBundleByIdQuery, V2GetBundleByIdQueryVariables>,
) {
  const options =
    baseOptions === Apollo.skipToken ? baseOptions : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<V2GetBundleByIdQuery, V2GetBundleByIdQueryVariables>(
    V2GetBundleByIdDocument,
    options,
  );
}
export type V2GetBundleByIdQueryHookResult = ReturnType<typeof useV2GetBundleByIdQuery>;
export type V2GetBundleByIdLazyQueryHookResult = ReturnType<typeof useV2GetBundleByIdLazyQuery>;
export type V2GetBundleByIdSuspenseQueryHookResult = ReturnType<
  typeof useV2GetBundleByIdSuspenseQuery
>;
export type V2GetBundleByIdQueryResult = Apollo.QueryResult<
  V2GetBundleByIdQuery,
  V2GetBundleByIdQueryVariables
>;
export const V2GetBundleBySlugDocument = gql`
    query V2GetBundleBySlug($localizationId: String!, $slug: String!, $bundleId: String) {
  v2GetBundleBySlug(localizationId: $localizationId, slug: $slug) {
    ...V2Bundle
  }
}
    ${V2BundleFragmentDoc}`;

/**
 * __useV2GetBundleBySlugQuery__
 *
 * To run a query within a React component, call `useV2GetBundleBySlugQuery` and pass it any options that fit your needs.
 * When your component renders, `useV2GetBundleBySlugQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useV2GetBundleBySlugQuery({
 *   variables: {
 *      localizationId: // value for 'localizationId'
 *      slug: // value for 'slug'
 *      bundleId: // value for 'bundleId'
 *   },
 * });
 */
export function useV2GetBundleBySlugQuery(
  baseOptions: Apollo.QueryHookOptions<V2GetBundleBySlugQuery, V2GetBundleBySlugQueryVariables> &
    ({ variables: V2GetBundleBySlugQueryVariables; skip?: boolean } | { skip: boolean }),
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery<V2GetBundleBySlugQuery, V2GetBundleBySlugQueryVariables>(
    V2GetBundleBySlugDocument,
    options,
  );
}
export function useV2GetBundleBySlugLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<
    V2GetBundleBySlugQuery,
    V2GetBundleBySlugQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<V2GetBundleBySlugQuery, V2GetBundleBySlugQueryVariables>(
    V2GetBundleBySlugDocument,
    options,
  );
}
export function useV2GetBundleBySlugSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | Apollo.SuspenseQueryHookOptions<V2GetBundleBySlugQuery, V2GetBundleBySlugQueryVariables>,
) {
  const options =
    baseOptions === Apollo.skipToken ? baseOptions : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<V2GetBundleBySlugQuery, V2GetBundleBySlugQueryVariables>(
    V2GetBundleBySlugDocument,
    options,
  );
}
export type V2GetBundleBySlugQueryHookResult = ReturnType<typeof useV2GetBundleBySlugQuery>;
export type V2GetBundleBySlugLazyQueryHookResult = ReturnType<typeof useV2GetBundleBySlugLazyQuery>;
export type V2GetBundleBySlugSuspenseQueryHookResult = ReturnType<
  typeof useV2GetBundleBySlugSuspenseQuery
>;
export type V2GetBundleBySlugQueryResult = Apollo.QueryResult<
  V2GetBundleBySlugQuery,
  V2GetBundleBySlugQueryVariables
>;
export const EstimateProcessingFeeDocument = gql`
    query EstimateProcessingFee($reservationId: String!) {
  estimateProcessingFee(reservationId: $reservationId) {
    ...OrderProcessingFee
  }
}
    ${OrderProcessingFeeFragmentDoc}`;

/**
 * __useEstimateProcessingFeeQuery__
 *
 * To run a query within a React component, call `useEstimateProcessingFeeQuery` and pass it any options that fit your needs.
 * When your component renders, `useEstimateProcessingFeeQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useEstimateProcessingFeeQuery({
 *   variables: {
 *      reservationId: // value for 'reservationId'
 *   },
 * });
 */
export function useEstimateProcessingFeeQuery(
  baseOptions: Apollo.QueryHookOptions<
    EstimateProcessingFeeQuery,
    EstimateProcessingFeeQueryVariables
  > &
    ({ variables: EstimateProcessingFeeQueryVariables; skip?: boolean } | { skip: boolean }),
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery<EstimateProcessingFeeQuery, EstimateProcessingFeeQueryVariables>(
    EstimateProcessingFeeDocument,
    options,
  );
}
export function useEstimateProcessingFeeLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<
    EstimateProcessingFeeQuery,
    EstimateProcessingFeeQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<EstimateProcessingFeeQuery, EstimateProcessingFeeQueryVariables>(
    EstimateProcessingFeeDocument,
    options,
  );
}
export function useEstimateProcessingFeeSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | Apollo.SuspenseQueryHookOptions<
        EstimateProcessingFeeQuery,
        EstimateProcessingFeeQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken ? baseOptions : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<EstimateProcessingFeeQuery, EstimateProcessingFeeQueryVariables>(
    EstimateProcessingFeeDocument,
    options,
  );
}
export type EstimateProcessingFeeQueryHookResult = ReturnType<typeof useEstimateProcessingFeeQuery>;
export type EstimateProcessingFeeLazyQueryHookResult = ReturnType<
  typeof useEstimateProcessingFeeLazyQuery
>;
export type EstimateProcessingFeeSuspenseQueryHookResult = ReturnType<
  typeof useEstimateProcessingFeeSuspenseQuery
>;
export type EstimateProcessingFeeQueryResult = Apollo.QueryResult<
  EstimateProcessingFeeQuery,
  EstimateProcessingFeeQueryVariables
>;
export const EstimateSubscriptionProcessingFeeDocument = gql`
    query EstimateSubscriptionProcessingFee($subscriptionId: String!) {
  estimateSubscriptionProcessingFee(subscriptionId: $subscriptionId) {
    ...SubscriptionProcessingFee
  }
}
    ${SubscriptionProcessingFeeFragmentDoc}`;

/**
 * __useEstimateSubscriptionProcessingFeeQuery__
 *
 * To run a query within a React component, call `useEstimateSubscriptionProcessingFeeQuery` and pass it any options that fit your needs.
 * When your component renders, `useEstimateSubscriptionProcessingFeeQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useEstimateSubscriptionProcessingFeeQuery({
 *   variables: {
 *      subscriptionId: // value for 'subscriptionId'
 *   },
 * });
 */
export function useEstimateSubscriptionProcessingFeeQuery(
  baseOptions: Apollo.QueryHookOptions<
    EstimateSubscriptionProcessingFeeQuery,
    EstimateSubscriptionProcessingFeeQueryVariables
  > &
    (
      | { variables: EstimateSubscriptionProcessingFeeQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery<
    EstimateSubscriptionProcessingFeeQuery,
    EstimateSubscriptionProcessingFeeQueryVariables
  >(EstimateSubscriptionProcessingFeeDocument, options);
}
export function useEstimateSubscriptionProcessingFeeLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<
    EstimateSubscriptionProcessingFeeQuery,
    EstimateSubscriptionProcessingFeeQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    EstimateSubscriptionProcessingFeeQuery,
    EstimateSubscriptionProcessingFeeQueryVariables
  >(EstimateSubscriptionProcessingFeeDocument, options);
}
export function useEstimateSubscriptionProcessingFeeSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | Apollo.SuspenseQueryHookOptions<
        EstimateSubscriptionProcessingFeeQuery,
        EstimateSubscriptionProcessingFeeQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken ? baseOptions : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    EstimateSubscriptionProcessingFeeQuery,
    EstimateSubscriptionProcessingFeeQueryVariables
  >(EstimateSubscriptionProcessingFeeDocument, options);
}
export type EstimateSubscriptionProcessingFeeQueryHookResult = ReturnType<
  typeof useEstimateSubscriptionProcessingFeeQuery
>;
export type EstimateSubscriptionProcessingFeeLazyQueryHookResult = ReturnType<
  typeof useEstimateSubscriptionProcessingFeeLazyQuery
>;
export type EstimateSubscriptionProcessingFeeSuspenseQueryHookResult = ReturnType<
  typeof useEstimateSubscriptionProcessingFeeSuspenseQuery
>;
export type EstimateSubscriptionProcessingFeeQueryResult = Apollo.QueryResult<
  EstimateSubscriptionProcessingFeeQuery,
  EstimateSubscriptionProcessingFeeQueryVariables
>;
export const EstimateReservationRescheduleDocument = gql`
    query EstimateReservationReschedule($reservationId: String!, $data: EstimateReservationRescheduleInput!) {
  estimateReservationReschedule(reservationId: $reservationId, data: $data) {
    ...OrderProcessingFee
  }
}
    ${OrderProcessingFeeFragmentDoc}`;

/**
 * __useEstimateReservationRescheduleQuery__
 *
 * To run a query within a React component, call `useEstimateReservationRescheduleQuery` and pass it any options that fit your needs.
 * When your component renders, `useEstimateReservationRescheduleQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useEstimateReservationRescheduleQuery({
 *   variables: {
 *      reservationId: // value for 'reservationId'
 *      data: // value for 'data'
 *   },
 * });
 */
export function useEstimateReservationRescheduleQuery(
  baseOptions: Apollo.QueryHookOptions<
    EstimateReservationRescheduleQuery,
    EstimateReservationRescheduleQueryVariables
  > &
    (
      | { variables: EstimateReservationRescheduleQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery<
    EstimateReservationRescheduleQuery,
    EstimateReservationRescheduleQueryVariables
  >(EstimateReservationRescheduleDocument, options);
}
export function useEstimateReservationRescheduleLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<
    EstimateReservationRescheduleQuery,
    EstimateReservationRescheduleQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    EstimateReservationRescheduleQuery,
    EstimateReservationRescheduleQueryVariables
  >(EstimateReservationRescheduleDocument, options);
}
export function useEstimateReservationRescheduleSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | Apollo.SuspenseQueryHookOptions<
        EstimateReservationRescheduleQuery,
        EstimateReservationRescheduleQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken ? baseOptions : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    EstimateReservationRescheduleQuery,
    EstimateReservationRescheduleQueryVariables
  >(EstimateReservationRescheduleDocument, options);
}
export type EstimateReservationRescheduleQueryHookResult = ReturnType<
  typeof useEstimateReservationRescheduleQuery
>;
export type EstimateReservationRescheduleLazyQueryHookResult = ReturnType<
  typeof useEstimateReservationRescheduleLazyQuery
>;
export type EstimateReservationRescheduleSuspenseQueryHookResult = ReturnType<
  typeof useEstimateReservationRescheduleSuspenseQuery
>;
export type EstimateReservationRescheduleQueryResult = Apollo.QueryResult<
  EstimateReservationRescheduleQuery,
  EstimateReservationRescheduleQueryVariables
>;
export const EstimateReservationAdditionalServiceEditDocument = gql`
    query EstimateReservationAdditionalServiceEdit($reservationId: String!, $data: EstimateReservationAdditionalServiceEditInput!, $bundleId: String) {
  estimateReservationAdditionalServiceEdit(
    reservationId: $reservationId
    data: $data
  ) {
    newPrice
    newTimeConsumption
    oldPrice
    oldTimeConsumption
    additional {
      ...OrderService
    }
  }
}
    ${OrderServiceFragmentDoc}`;

/**
 * __useEstimateReservationAdditionalServiceEditQuery__
 *
 * To run a query within a React component, call `useEstimateReservationAdditionalServiceEditQuery` and pass it any options that fit your needs.
 * When your component renders, `useEstimateReservationAdditionalServiceEditQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useEstimateReservationAdditionalServiceEditQuery({
 *   variables: {
 *      reservationId: // value for 'reservationId'
 *      data: // value for 'data'
 *      bundleId: // value for 'bundleId'
 *   },
 * });
 */
export function useEstimateReservationAdditionalServiceEditQuery(
  baseOptions: Apollo.QueryHookOptions<
    EstimateReservationAdditionalServiceEditQuery,
    EstimateReservationAdditionalServiceEditQueryVariables
  > &
    (
      | { variables: EstimateReservationAdditionalServiceEditQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery<
    EstimateReservationAdditionalServiceEditQuery,
    EstimateReservationAdditionalServiceEditQueryVariables
  >(EstimateReservationAdditionalServiceEditDocument, options);
}
export function useEstimateReservationAdditionalServiceEditLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<
    EstimateReservationAdditionalServiceEditQuery,
    EstimateReservationAdditionalServiceEditQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    EstimateReservationAdditionalServiceEditQuery,
    EstimateReservationAdditionalServiceEditQueryVariables
  >(EstimateReservationAdditionalServiceEditDocument, options);
}
export function useEstimateReservationAdditionalServiceEditSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | Apollo.SuspenseQueryHookOptions<
        EstimateReservationAdditionalServiceEditQuery,
        EstimateReservationAdditionalServiceEditQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken ? baseOptions : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    EstimateReservationAdditionalServiceEditQuery,
    EstimateReservationAdditionalServiceEditQueryVariables
  >(EstimateReservationAdditionalServiceEditDocument, options);
}
export type EstimateReservationAdditionalServiceEditQueryHookResult = ReturnType<
  typeof useEstimateReservationAdditionalServiceEditQuery
>;
export type EstimateReservationAdditionalServiceEditLazyQueryHookResult = ReturnType<
  typeof useEstimateReservationAdditionalServiceEditLazyQuery
>;
export type EstimateReservationAdditionalServiceEditSuspenseQueryHookResult = ReturnType<
  typeof useEstimateReservationAdditionalServiceEditSuspenseQuery
>;
export type EstimateReservationAdditionalServiceEditQueryResult = Apollo.QueryResult<
  EstimateReservationAdditionalServiceEditQuery,
  EstimateReservationAdditionalServiceEditQueryVariables
>;
export const EstimateReservationAttributeEditDocument = gql`
    query EstimateReservationAttributeEdit($reservationId: String!, $data: EstimateReservationAttributeEditInput!) {
  estimateReservationAttributeEdit(reservationId: $reservationId, data: $data) {
    newPrice
    newTimeConsumption
    oldPrice
    oldTimeConsumption
    questions {
      ...OrderAttribute
    }
  }
}
    ${OrderAttributeFragmentDoc}`;

/**
 * __useEstimateReservationAttributeEditQuery__
 *
 * To run a query within a React component, call `useEstimateReservationAttributeEditQuery` and pass it any options that fit your needs.
 * When your component renders, `useEstimateReservationAttributeEditQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useEstimateReservationAttributeEditQuery({
 *   variables: {
 *      reservationId: // value for 'reservationId'
 *      data: // value for 'data'
 *   },
 * });
 */
export function useEstimateReservationAttributeEditQuery(
  baseOptions: Apollo.QueryHookOptions<
    EstimateReservationAttributeEditQuery,
    EstimateReservationAttributeEditQueryVariables
  > &
    (
      | { variables: EstimateReservationAttributeEditQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery<
    EstimateReservationAttributeEditQuery,
    EstimateReservationAttributeEditQueryVariables
  >(EstimateReservationAttributeEditDocument, options);
}
export function useEstimateReservationAttributeEditLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<
    EstimateReservationAttributeEditQuery,
    EstimateReservationAttributeEditQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    EstimateReservationAttributeEditQuery,
    EstimateReservationAttributeEditQueryVariables
  >(EstimateReservationAttributeEditDocument, options);
}
export function useEstimateReservationAttributeEditSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | Apollo.SuspenseQueryHookOptions<
        EstimateReservationAttributeEditQuery,
        EstimateReservationAttributeEditQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken ? baseOptions : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    EstimateReservationAttributeEditQuery,
    EstimateReservationAttributeEditQueryVariables
  >(EstimateReservationAttributeEditDocument, options);
}
export type EstimateReservationAttributeEditQueryHookResult = ReturnType<
  typeof useEstimateReservationAttributeEditQuery
>;
export type EstimateReservationAttributeEditLazyQueryHookResult = ReturnType<
  typeof useEstimateReservationAttributeEditLazyQuery
>;
export type EstimateReservationAttributeEditSuspenseQueryHookResult = ReturnType<
  typeof useEstimateReservationAttributeEditSuspenseQuery
>;
export type EstimateReservationAttributeEditQueryResult = Apollo.QueryResult<
  EstimateReservationAttributeEditQuery,
  EstimateReservationAttributeEditQueryVariables
>;
export const GetPaymentDocument = gql`
    query GetPayment($paymentId: String!) {
  getPayment(paymentId: $paymentId) {
    ...Payment
  }
}
    ${PaymentFragmentDoc}`;

/**
 * __useGetPaymentQuery__
 *
 * To run a query within a React component, call `useGetPaymentQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetPaymentQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetPaymentQuery({
 *   variables: {
 *      paymentId: // value for 'paymentId'
 *   },
 * });
 */
export function useGetPaymentQuery(
  baseOptions: Apollo.QueryHookOptions<GetPaymentQuery, GetPaymentQueryVariables> &
    ({ variables: GetPaymentQueryVariables; skip?: boolean } | { skip: boolean }),
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery<GetPaymentQuery, GetPaymentQueryVariables>(GetPaymentDocument, options);
}
export function useGetPaymentLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<GetPaymentQuery, GetPaymentQueryVariables>,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<GetPaymentQuery, GetPaymentQueryVariables>(
    GetPaymentDocument,
    options,
  );
}
export function useGetPaymentSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | Apollo.SuspenseQueryHookOptions<GetPaymentQuery, GetPaymentQueryVariables>,
) {
  const options =
    baseOptions === Apollo.skipToken ? baseOptions : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<GetPaymentQuery, GetPaymentQueryVariables>(
    GetPaymentDocument,
    options,
  );
}
export type GetPaymentQueryHookResult = ReturnType<typeof useGetPaymentQuery>;
export type GetPaymentLazyQueryHookResult = ReturnType<typeof useGetPaymentLazyQuery>;
export type GetPaymentSuspenseQueryHookResult = ReturnType<typeof useGetPaymentSuspenseQuery>;
export type GetPaymentQueryResult = Apollo.QueryResult<GetPaymentQuery, GetPaymentQueryVariables>;
export const ChangePaymentMethodDocument = gql`
    mutation ChangePaymentMethod($paymentId: String!, $paymentMethodId: String!, $reservationId: String!) {
  changePaymentMethod(
    paymentId: $paymentId
    paymentMethodId: $paymentMethodId
    reservationId: $reservationId
  ) {
    ...Payment
  }
}
    ${PaymentFragmentDoc}`;
export type ChangePaymentMethodMutationFn = Apollo.MutationFunction<
  ChangePaymentMethodMutation,
  ChangePaymentMethodMutationVariables
>;

/**
 * __useChangePaymentMethodMutation__
 *
 * To run a mutation, you first call `useChangePaymentMethodMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useChangePaymentMethodMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [changePaymentMethodMutation, { data, loading, error }] = useChangePaymentMethodMutation({
 *   variables: {
 *      paymentId: // value for 'paymentId'
 *      paymentMethodId: // value for 'paymentMethodId'
 *      reservationId: // value for 'reservationId'
 *   },
 * });
 */
export function useChangePaymentMethodMutation(
  baseOptions?: Apollo.MutationHookOptions<
    ChangePaymentMethodMutation,
    ChangePaymentMethodMutationVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<ChangePaymentMethodMutation, ChangePaymentMethodMutationVariables>(
    ChangePaymentMethodDocument,
    options,
  );
}
export type ChangePaymentMethodMutationHookResult = ReturnType<
  typeof useChangePaymentMethodMutation
>;
export type ChangePaymentMethodMutationResult = Apollo.MutationResult<ChangePaymentMethodMutation>;
export type ChangePaymentMethodMutationOptions = Apollo.BaseMutationOptions<
  ChangePaymentMethodMutation,
  ChangePaymentMethodMutationVariables
>;
export const DeclareRescheduleReservationDocument = gql`
    mutation DeclareRescheduleReservation($reservationId: String!, $data: EstimateReservationRescheduleInput!) {
  declareRescheduleReservation(reservationId: $reservationId, data: $data) {
    ... on NotFulfilledSwap {
      ...SwapFields
    }
    ... on SwapRequiresAction {
      ...SwapFields
    }
  }
}
    ${SwapFieldsFragmentDoc}`;
export type DeclareRescheduleReservationMutationFn = Apollo.MutationFunction<
  DeclareRescheduleReservationMutation,
  DeclareRescheduleReservationMutationVariables
>;

/**
 * __useDeclareRescheduleReservationMutation__
 *
 * To run a mutation, you first call `useDeclareRescheduleReservationMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDeclareRescheduleReservationMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [declareRescheduleReservationMutation, { data, loading, error }] = useDeclareRescheduleReservationMutation({
 *   variables: {
 *      reservationId: // value for 'reservationId'
 *      data: // value for 'data'
 *   },
 * });
 */
export function useDeclareRescheduleReservationMutation(
  baseOptions?: Apollo.MutationHookOptions<
    DeclareRescheduleReservationMutation,
    DeclareRescheduleReservationMutationVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<
    DeclareRescheduleReservationMutation,
    DeclareRescheduleReservationMutationVariables
  >(DeclareRescheduleReservationDocument, options);
}
export type DeclareRescheduleReservationMutationHookResult = ReturnType<
  typeof useDeclareRescheduleReservationMutation
>;
export type DeclareRescheduleReservationMutationResult =
  Apollo.MutationResult<DeclareRescheduleReservationMutation>;
export type DeclareRescheduleReservationMutationOptions = Apollo.BaseMutationOptions<
  DeclareRescheduleReservationMutation,
  DeclareRescheduleReservationMutationVariables
>;
export const CompleteRescheduleDocument = gql`
    mutation CompleteReschedule($swapId: String!) {
  completeReschedule(swapId: $swapId) {
    id
    toReservation {
      id
    }
  }
}
    `;
export type CompleteRescheduleMutationFn = Apollo.MutationFunction<
  CompleteRescheduleMutation,
  CompleteRescheduleMutationVariables
>;

/**
 * __useCompleteRescheduleMutation__
 *
 * To run a mutation, you first call `useCompleteRescheduleMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCompleteRescheduleMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [completeRescheduleMutation, { data, loading, error }] = useCompleteRescheduleMutation({
 *   variables: {
 *      swapId: // value for 'swapId'
 *   },
 * });
 */
export function useCompleteRescheduleMutation(
  baseOptions?: Apollo.MutationHookOptions<
    CompleteRescheduleMutation,
    CompleteRescheduleMutationVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<CompleteRescheduleMutation, CompleteRescheduleMutationVariables>(
    CompleteRescheduleDocument,
    options,
  );
}
export type CompleteRescheduleMutationHookResult = ReturnType<typeof useCompleteRescheduleMutation>;
export type CompleteRescheduleMutationResult = Apollo.MutationResult<CompleteRescheduleMutation>;
export type CompleteRescheduleMutationOptions = Apollo.BaseMutationOptions<
  CompleteRescheduleMutation,
  CompleteRescheduleMutationVariables
>;
export const DeclareReservationAttributeEditDocument = gql`
    mutation DeclareReservationAttributeEdit($reservationId: String!, $data: EstimateReservationAttributeEditInput!) {
  declareReservationAttributeEdit(reservationId: $reservationId, data: $data) {
    ... on NotFulfilledSwap {
      ...SwapFields
    }
    ... on SwapRequiresAction {
      ...SwapFields
    }
  }
}
    ${SwapFieldsFragmentDoc}`;
export type DeclareReservationAttributeEditMutationFn = Apollo.MutationFunction<
  DeclareReservationAttributeEditMutation,
  DeclareReservationAttributeEditMutationVariables
>;

/**
 * __useDeclareReservationAttributeEditMutation__
 *
 * To run a mutation, you first call `useDeclareReservationAttributeEditMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDeclareReservationAttributeEditMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [declareReservationAttributeEditMutation, { data, loading, error }] = useDeclareReservationAttributeEditMutation({
 *   variables: {
 *      reservationId: // value for 'reservationId'
 *      data: // value for 'data'
 *   },
 * });
 */
export function useDeclareReservationAttributeEditMutation(
  baseOptions?: Apollo.MutationHookOptions<
    DeclareReservationAttributeEditMutation,
    DeclareReservationAttributeEditMutationVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<
    DeclareReservationAttributeEditMutation,
    DeclareReservationAttributeEditMutationVariables
  >(DeclareReservationAttributeEditDocument, options);
}
export type DeclareReservationAttributeEditMutationHookResult = ReturnType<
  typeof useDeclareReservationAttributeEditMutation
>;
export type DeclareReservationAttributeEditMutationResult =
  Apollo.MutationResult<DeclareReservationAttributeEditMutation>;
export type DeclareReservationAttributeEditMutationOptions = Apollo.BaseMutationOptions<
  DeclareReservationAttributeEditMutation,
  DeclareReservationAttributeEditMutationVariables
>;
export const CompleteAttributeEditDocument = gql`
    mutation CompleteAttributeEdit($swapId: String!) {
  completeAttributeEdit(swapId: $swapId) {
    id
    toReservation {
      id
    }
  }
}
    `;
export type CompleteAttributeEditMutationFn = Apollo.MutationFunction<
  CompleteAttributeEditMutation,
  CompleteAttributeEditMutationVariables
>;

/**
 * __useCompleteAttributeEditMutation__
 *
 * To run a mutation, you first call `useCompleteAttributeEditMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCompleteAttributeEditMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [completeAttributeEditMutation, { data, loading, error }] = useCompleteAttributeEditMutation({
 *   variables: {
 *      swapId: // value for 'swapId'
 *   },
 * });
 */
export function useCompleteAttributeEditMutation(
  baseOptions?: Apollo.MutationHookOptions<
    CompleteAttributeEditMutation,
    CompleteAttributeEditMutationVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<CompleteAttributeEditMutation, CompleteAttributeEditMutationVariables>(
    CompleteAttributeEditDocument,
    options,
  );
}
export type CompleteAttributeEditMutationHookResult = ReturnType<
  typeof useCompleteAttributeEditMutation
>;
export type CompleteAttributeEditMutationResult =
  Apollo.MutationResult<CompleteAttributeEditMutation>;
export type CompleteAttributeEditMutationOptions = Apollo.BaseMutationOptions<
  CompleteAttributeEditMutation,
  CompleteAttributeEditMutationVariables
>;
export const DeclareReservationAdditionalEditDocument = gql`
    mutation DeclareReservationAdditionalEdit($reservationId: String!, $data: EstimateReservationAdditionalServiceEditInput!) {
  declareReservationAdditionalEdit(reservationId: $reservationId, data: $data) {
    ... on NotFulfilledSwap {
      ...SwapFields
    }
    ... on SwapRequiresAction {
      ...SwapFields
    }
  }
}
    ${SwapFieldsFragmentDoc}`;
export type DeclareReservationAdditionalEditMutationFn = Apollo.MutationFunction<
  DeclareReservationAdditionalEditMutation,
  DeclareReservationAdditionalEditMutationVariables
>;

/**
 * __useDeclareReservationAdditionalEditMutation__
 *
 * To run a mutation, you first call `useDeclareReservationAdditionalEditMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useDeclareReservationAdditionalEditMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [declareReservationAdditionalEditMutation, { data, loading, error }] = useDeclareReservationAdditionalEditMutation({
 *   variables: {
 *      reservationId: // value for 'reservationId'
 *      data: // value for 'data'
 *   },
 * });
 */
export function useDeclareReservationAdditionalEditMutation(
  baseOptions?: Apollo.MutationHookOptions<
    DeclareReservationAdditionalEditMutation,
    DeclareReservationAdditionalEditMutationVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<
    DeclareReservationAdditionalEditMutation,
    DeclareReservationAdditionalEditMutationVariables
  >(DeclareReservationAdditionalEditDocument, options);
}
export type DeclareReservationAdditionalEditMutationHookResult = ReturnType<
  typeof useDeclareReservationAdditionalEditMutation
>;
export type DeclareReservationAdditionalEditMutationResult =
  Apollo.MutationResult<DeclareReservationAdditionalEditMutation>;
export type DeclareReservationAdditionalEditMutationOptions = Apollo.BaseMutationOptions<
  DeclareReservationAdditionalEditMutation,
  DeclareReservationAdditionalEditMutationVariables
>;
export const CompleteAdditionalEditDocument = gql`
    mutation CompleteAdditionalEdit($swapId: String!) {
  completeAdditionalEdit(swapId: $swapId) {
    id
    toReservation {
      id
    }
  }
}
    `;
export type CompleteAdditionalEditMutationFn = Apollo.MutationFunction<
  CompleteAdditionalEditMutation,
  CompleteAdditionalEditMutationVariables
>;

/**
 * __useCompleteAdditionalEditMutation__
 *
 * To run a mutation, you first call `useCompleteAdditionalEditMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCompleteAdditionalEditMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [completeAdditionalEditMutation, { data, loading, error }] = useCompleteAdditionalEditMutation({
 *   variables: {
 *      swapId: // value for 'swapId'
 *   },
 * });
 */
export function useCompleteAdditionalEditMutation(
  baseOptions?: Apollo.MutationHookOptions<
    CompleteAdditionalEditMutation,
    CompleteAdditionalEditMutationVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<
    CompleteAdditionalEditMutation,
    CompleteAdditionalEditMutationVariables
  >(CompleteAdditionalEditDocument, options);
}
export type CompleteAdditionalEditMutationHookResult = ReturnType<
  typeof useCompleteAdditionalEditMutation
>;
export type CompleteAdditionalEditMutationResult =
  Apollo.MutationResult<CompleteAdditionalEditMutation>;
export type CompleteAdditionalEditMutationOptions = Apollo.BaseMutationOptions<
  CompleteAdditionalEditMutation,
  CompleteAdditionalEditMutationVariables
>;
export const UpdateReservationCustomerNoteDocument = gql`
    mutation UpdateReservationCustomerNote($reservationId: String!, $note: String!) {
  updateReservationCustomerNote(reservationId: $reservationId, note: $note) {
    id
  }
}
    `;
export type UpdateReservationCustomerNoteMutationFn = Apollo.MutationFunction<
  UpdateReservationCustomerNoteMutation,
  UpdateReservationCustomerNoteMutationVariables
>;

/**
 * __useUpdateReservationCustomerNoteMutation__
 *
 * To run a mutation, you first call `useUpdateReservationCustomerNoteMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useUpdateReservationCustomerNoteMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [updateReservationCustomerNoteMutation, { data, loading, error }] = useUpdateReservationCustomerNoteMutation({
 *   variables: {
 *      reservationId: // value for 'reservationId'
 *      note: // value for 'note'
 *   },
 * });
 */
export function useUpdateReservationCustomerNoteMutation(
  baseOptions?: Apollo.MutationHookOptions<
    UpdateReservationCustomerNoteMutation,
    UpdateReservationCustomerNoteMutationVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<
    UpdateReservationCustomerNoteMutation,
    UpdateReservationCustomerNoteMutationVariables
  >(UpdateReservationCustomerNoteDocument, options);
}
export type UpdateReservationCustomerNoteMutationHookResult = ReturnType<
  typeof useUpdateReservationCustomerNoteMutation
>;
export type UpdateReservationCustomerNoteMutationResult =
  Apollo.MutationResult<UpdateReservationCustomerNoteMutation>;
export type UpdateReservationCustomerNoteMutationOptions = Apollo.BaseMutationOptions<
  UpdateReservationCustomerNoteMutation,
  UpdateReservationCustomerNoteMutationVariables
>;
export const CancelReservationDocument = gql`
    mutation CancelReservation($reservationId: String!, $reason: String!) {
  cancelReservation(reservationId: $reservationId, reason: $reason) {
    id
  }
}
    `;
export type CancelReservationMutationFn = Apollo.MutationFunction<
  CancelReservationMutation,
  CancelReservationMutationVariables
>;

/**
 * __useCancelReservationMutation__
 *
 * To run a mutation, you first call `useCancelReservationMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCancelReservationMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [cancelReservationMutation, { data, loading, error }] = useCancelReservationMutation({
 *   variables: {
 *      reservationId: // value for 'reservationId'
 *      reason: // value for 'reason'
 *   },
 * });
 */
export function useCancelReservationMutation(
  baseOptions?: Apollo.MutationHookOptions<
    CancelReservationMutation,
    CancelReservationMutationVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<CancelReservationMutation, CancelReservationMutationVariables>(
    CancelReservationDocument,
    options,
  );
}
export type CancelReservationMutationHookResult = ReturnType<typeof useCancelReservationMutation>;
export type CancelReservationMutationResult = Apollo.MutationResult<CancelReservationMutation>;
export type CancelReservationMutationOptions = Apollo.BaseMutationOptions<
  CancelReservationMutation,
  CancelReservationMutationVariables
>;
export const V2CancelSubscriptionDocument = gql`
    mutation V2CancelSubscription($subscriptionId: String!, $reason: String!) {
  v2CancelSubscription(subscriptionId: $subscriptionId, reason: $reason)
}
    `;
export type V2CancelSubscriptionMutationFn = Apollo.MutationFunction<
  V2CancelSubscriptionMutation,
  V2CancelSubscriptionMutationVariables
>;

/**
 * __useV2CancelSubscriptionMutation__
 *
 * To run a mutation, you first call `useV2CancelSubscriptionMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useV2CancelSubscriptionMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [v2CancelSubscriptionMutation, { data, loading, error }] = useV2CancelSubscriptionMutation({
 *   variables: {
 *      subscriptionId: // value for 'subscriptionId'
 *      reason: // value for 'reason'
 *   },
 * });
 */
export function useV2CancelSubscriptionMutation(
  baseOptions?: Apollo.MutationHookOptions<
    V2CancelSubscriptionMutation,
    V2CancelSubscriptionMutationVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<V2CancelSubscriptionMutation, V2CancelSubscriptionMutationVariables>(
    V2CancelSubscriptionDocument,
    options,
  );
}
export type V2CancelSubscriptionMutationHookResult = ReturnType<
  typeof useV2CancelSubscriptionMutation
>;
export type V2CancelSubscriptionMutationResult =
  Apollo.MutationResult<V2CancelSubscriptionMutation>;
export type V2CancelSubscriptionMutationOptions = Apollo.BaseMutationOptions<
  V2CancelSubscriptionMutation,
  V2CancelSubscriptionMutationVariables
>;
export const V2DraftCheckoutDocument = gql`
    mutation V2DraftCheckout($data: v2DraftCheckoutInput!) {
  v2DraftCheckout(data: $data) {
    ...V2Checkout
  }
}
    ${V2CheckoutFragmentDoc}`;
export type V2DraftCheckoutMutationFn = Apollo.MutationFunction<
  V2DraftCheckoutMutation,
  V2DraftCheckoutMutationVariables
>;

/**
 * __useV2DraftCheckoutMutation__
 *
 * To run a mutation, you first call `useV2DraftCheckoutMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useV2DraftCheckoutMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [v2DraftCheckoutMutation, { data, loading, error }] = useV2DraftCheckoutMutation({
 *   variables: {
 *      data: // value for 'data'
 *   },
 * });
 */
export function useV2DraftCheckoutMutation(
  baseOptions?: Apollo.MutationHookOptions<
    V2DraftCheckoutMutation,
    V2DraftCheckoutMutationVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<V2DraftCheckoutMutation, V2DraftCheckoutMutationVariables>(
    V2DraftCheckoutDocument,
    options,
  );
}
export type V2DraftCheckoutMutationHookResult = ReturnType<typeof useV2DraftCheckoutMutation>;
export type V2DraftCheckoutMutationResult = Apollo.MutationResult<V2DraftCheckoutMutation>;
export type V2DraftCheckoutMutationOptions = Apollo.BaseMutationOptions<
  V2DraftCheckoutMutation,
  V2DraftCheckoutMutationVariables
>;
export const ConfirmCheckoutDocument = gql`
    mutation ConfirmCheckout($checkoutId: String!) {
  confirmCheckout(checkoutId: $checkoutId) {
    id
    type
  }
}
    `;
export type ConfirmCheckoutMutationFn = Apollo.MutationFunction<
  ConfirmCheckoutMutation,
  ConfirmCheckoutMutationVariables
>;

/**
 * __useConfirmCheckoutMutation__
 *
 * To run a mutation, you first call `useConfirmCheckoutMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useConfirmCheckoutMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [confirmCheckoutMutation, { data, loading, error }] = useConfirmCheckoutMutation({
 *   variables: {
 *      checkoutId: // value for 'checkoutId'
 *   },
 * });
 */
export function useConfirmCheckoutMutation(
  baseOptions?: Apollo.MutationHookOptions<
    ConfirmCheckoutMutation,
    ConfirmCheckoutMutationVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useMutation<ConfirmCheckoutMutation, ConfirmCheckoutMutationVariables>(
    ConfirmCheckoutDocument,
    options,
  );
}
export type ConfirmCheckoutMutationHookResult = ReturnType<typeof useConfirmCheckoutMutation>;
export type ConfirmCheckoutMutationResult = Apollo.MutationResult<ConfirmCheckoutMutation>;
export type ConfirmCheckoutMutationOptions = Apollo.BaseMutationOptions<
  ConfirmCheckoutMutation,
  ConfirmCheckoutMutationVariables
>;
export const V2GetCheckoutDocument = gql`
    query V2GetCheckout {
  v2GetCheckout {
    ...V2Checkout
  }
}
    ${V2CheckoutFragmentDoc}`;

/**
 * __useV2GetCheckoutQuery__
 *
 * To run a query within a React component, call `useV2GetCheckoutQuery` and pass it any options that fit your needs.
 * When your component renders, `useV2GetCheckoutQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useV2GetCheckoutQuery({
 *   variables: {
 *   },
 * });
 */
export function useV2GetCheckoutQuery(
  baseOptions?: Apollo.QueryHookOptions<V2GetCheckoutQuery, V2GetCheckoutQueryVariables>,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery<V2GetCheckoutQuery, V2GetCheckoutQueryVariables>(
    V2GetCheckoutDocument,
    options,
  );
}
export function useV2GetCheckoutLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<V2GetCheckoutQuery, V2GetCheckoutQueryVariables>,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<V2GetCheckoutQuery, V2GetCheckoutQueryVariables>(
    V2GetCheckoutDocument,
    options,
  );
}
export function useV2GetCheckoutSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | Apollo.SuspenseQueryHookOptions<V2GetCheckoutQuery, V2GetCheckoutQueryVariables>,
) {
  const options =
    baseOptions === Apollo.skipToken ? baseOptions : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<V2GetCheckoutQuery, V2GetCheckoutQueryVariables>(
    V2GetCheckoutDocument,
    options,
  );
}
export type V2GetCheckoutQueryHookResult = ReturnType<typeof useV2GetCheckoutQuery>;
export type V2GetCheckoutLazyQueryHookResult = ReturnType<typeof useV2GetCheckoutLazyQuery>;
export type V2GetCheckoutSuspenseQueryHookResult = ReturnType<typeof useV2GetCheckoutSuspenseQuery>;
export type V2GetCheckoutQueryResult = Apollo.QueryResult<
  V2GetCheckoutQuery,
  V2GetCheckoutQueryVariables
>;
